import { useEffect, useRef, useState } from 'react';
import ReactPlayer from 'react-player/vimeo';
import {
  Box,
  IconButton,
  Slider,
  Stack,
  Typography,
  Skeleton,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import SettingsIcon from '@mui/icons-material/Settings';
import Replay5Icon from '@mui/icons-material/Replay5';
import { TrainingModuleEndingOverlay } from './TrainingModuleEndingOverlay';
import { useDocumentVisibility } from '../../../hooks/useDocumentVisibility';
import Cookies from 'js-cookie';
import { Snackbar } from '../../../components/Global/Snackbar/Snackbar';

export const TrainingModuleVideoPlayer = ({
  currentVideo,
  handleVideoCompleted,
  trainingCompleted,
  hasNext,
  nextVideo,
}) => {
  const playerRef = useRef(null);
  const [playbackAnchorEl, setPlaybackAnchorEl] = useState(null);
  const [playbackMenuOpen, setPlaybackMenuOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hover, setHover] = useState(false);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [showEndingOverlay, setShowEndingOverlay] = useState(false);
  const [snackbarDetails, setSnackbarDetails] = useState({
    open: false,
    status: '',
    message: '',
  });

  const getSavedResume = () => {
    const raw = Cookies.get(`resume_${currentVideo.ID}-${currentVideo.VideoTitle}`);
    return raw ? parseFloat(raw) : 0;
  };

  const getSavedProgress = () => {
    const raw = Cookies.get(`progress_${currentVideo.ID}-${currentVideo.VideoTitle}`);
    return raw ? parseFloat(raw) : 0;
  };

  const [played, setPlayed] = useState(getSavedResume);
  const [maxPlayed, setMaxPlayed] = useState(getSavedProgress);
  const resumeRef = useRef(getSavedResume());

  const handlePlaybackMenuOpen = (e) => {
    setPlaybackAnchorEl(e.currentTarget);
    setPlaybackMenuOpen(true);
  };

  const handlePlaybackMenuClose = () => {
    setPlaybackAnchorEl(null);
    setPlaybackMenuOpen(false);
  };

  const handlePlaybackMenuSelection = (playbackSpeed) => {
    setPlaybackRate(playbackSpeed);
    handlePlaybackMenuClose();
  };

  // monitor if user changes browser tabs, pause video if tab changes
  const isDocumentVisible = useDocumentVisibility();
  if (isDocumentVisible === false && isPlaying === true) {
    setIsPlaying(false);
  }

  const handleVideoEnd = () => {
    setIsPlaying(false);
    setShowEndingOverlay(true);
    handleVideoCompleted(currentVideo.ID);
  };

  useEffect(() => {
    if (!duration) return;

    const resumeRaw = Cookies.get(`resume_${currentVideo.ID}-${currentVideo.VideoTitle}`);
    const progressRaw = Cookies.get(`progress_${currentVideo.ID}-${currentVideo.VideoTitle}`);

    const resume = resumeRaw ? Math.ceil(resumeRaw) : null;
    const watched = progressRaw ? Math.ceil(progressRaw) : 0;

    if (resume !== null && resume !== 0) {
      resumeRef.current = resume;
    }

    if (resume === duration) {
      setShowEndingOverlay(true);
    }

    setMaxPlayed(watched);

    if (watched === duration) {
      handleVideoCompleted(currentVideo.ID);
    }

    if (!trainingCompleted && (resumeRef.current > 0 || watched === duration)) {
      setSnackbarDetails({
        ...snackbarDetails,
        status: 'info',
        message: 'We saved your progress!',
        open: true,
      });
    }
  }, [currentVideo, duration]);

  const togglePlayPause = () => {
    setIsPlaying((prev) => {
      if (prev) {
        playerRef.current.getInternalPlayer().pause();
      } else {
        playerRef.current.getInternalPlayer().play();
      }
      return !prev;
    });
  };

  const rewindVideo = () => {
    const newPlayed = Math.max(played - 5, 0);
    setPlayed(newPlayed);
    playerRef.current.seekTo(newPlayed);
  };

  const handleRestartVideo = () => {
    setShowEndingOverlay(false);
    Cookies.set(`resume_${currentVideo.ID}-${currentVideo.VideoTitle}`, '0', { expires: 5 });
    setPlayed(0);
    setIsPlaying(true);
    resumeRef.current = 0;

    if (playerRef.current) {
      playerRef.current.seekTo(0);
    }
  };

  const hasResumedRef = useRef(false);

  const handleProgress = (progress) => {
    const currentTime = progress.playedSeconds;

    if (!hasResumedRef.current && resumeRef.current !== null) {
      const resumeTime = resumeRef.current;
      if (Math.abs(currentTime - resumeTime) < 1) {
        hasResumedRef.current = true;
      } else {
        return;
      }
    }

    setPlayed(currentTime);
    Cookies.set(`resume_${currentVideo.ID}-${currentVideo.VideoTitle}`, currentTime, {
      expires: 5,
    });

    if (currentTime > maxPlayed && currentTime <= duration) {
      setMaxPlayed(currentTime);
      Cookies.set(`progress_${currentVideo.ID}-${currentVideo.VideoTitle}`, currentTime, {
        expires: 5,
      });
    }
  };

  const handleDuration = (duration) => {
    setDuration(duration);
  };

  return (
    <>
      <Snackbar
        open={snackbarDetails.open}
        duration={3000}
        message={snackbarDetails.message}
        status={snackbarDetails.status}
        onClose={() =>
          setSnackbarDetails({
            ...snackbarDetails,
            open: false,
          })
        }
      />
      <Typography variant="h5" sx={{ padding: '.5rem 0' }}>
        {currentVideo.VideoTitle}
      </Typography>
      <Box
        sx={{
          position: 'relative',
          aspectRatio: '16/9',
          cursor: 'pointer',
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        {currentVideo.VideoUrl ? (
          <>
            {showEndingOverlay ? (
              <TrainingModuleEndingOverlay
                handleRestartVideo={handleRestartVideo}
                hasNext={hasNext}
                trainingCompleted={trainingCompleted}
                handleVideoEnd={handleVideoEnd}
                nextVideo={nextVideo}
              />
            ) : (
              <>
                <ReactPlayer
                  ref={playerRef}
                  url={currentVideo.VideoUrl}
                  width="100%"
                  height="100%"
                  loop={false}
                  pip={false}
                  onEnded={() => handleVideoEnd()}
                  playing={isPlaying}
                  onReady={() => {
                    if (resumeRef.current && playerRef.current) {
                      playerRef.current.seekTo(resumeRef.current, 'seconds');
                      setPlayed(resumeRef.current);
                    }
                  }}
                  controls={false}
                  onProgress={handleProgress}
                  onDuration={handleDuration}
                  style={{ border: 'none' }}
                  playbackRate={playbackRate}
                />
                <IconButton
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    display: isPlaying ? (hover ? 'flex' : 'none') : 'flex',
                    bgcolor: 'grey.200',
                  }}
                  onClick={togglePlayPause}
                >
                  {isPlaying ? <PauseIcon fontSize="large" /> : <PlayArrowIcon fontSize="large" />}
                </IconButton>
                <Stack>
                  <Tooltip title="Rewind">
                    <IconButton
                      sx={{
                        position: 'absolute',
                        bottom: '10px',
                        left: '10px',
                        zIndex: 100,
                        bgcolor: 'rgba(255, 255, 255, 0.7)',
                      }}
                      onClick={rewindVideo}
                    >
                      <Replay5Icon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Playback Speed">
                    <IconButton
                      sx={{
                        position: 'absolute',
                        bottom: '10px',
                        left: '50px',
                        zIndex: 100,
                        bgcolor: 'rgba(255, 255, 255, 0.7)',
                      }}
                      id="playback-settings-button"
                      aria-controls={playbackMenuOpen ? 'playback-speed-button' : undefined}
                      aria-haspopup="true"
                      aria-expanded={playbackMenuOpen ? 'true' : undefined}
                      onClick={handlePlaybackMenuOpen}
                    >
                      <SettingsIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    id="playback-menu"
                    aria-labelledby="playback-settings-button"
                    anchorEl={playbackAnchorEl}
                    open={playbackMenuOpen}
                    onClose={handlePlaybackMenuClose}
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'center',
                    }}
                    transformOrigin={{
                      vertical: 'center',
                      horizontal: 'center',
                    }}
                  >
                    <MenuItem
                      selected={playbackRate === 0.5 ? true : false}
                      onClick={() => handlePlaybackMenuSelection(0.5)}
                    >
                      .5x
                    </MenuItem>
                    <MenuItem
                      selected={playbackRate === 0.75 ? true : false}
                      onClick={() => handlePlaybackMenuSelection(0.75)}
                    >
                      .75x
                    </MenuItem>
                    <MenuItem
                      selected={playbackRate === 1.0 ? true : false}
                      onClick={() => handlePlaybackMenuSelection(1.0)}
                    >
                      1.0x
                    </MenuItem>
                    <MenuItem
                      selected={playbackRate === 1.5 ? true : false}
                      onClick={() => handlePlaybackMenuSelection(1.5)}
                    >
                      1.5x
                    </MenuItem>
                  </Menu>
                  <Slider
                    value={played}
                    min={0}
                    max={duration}
                    disabled
                    sx={{
                      position: 'absolute',
                      bottom: '10px',
                      left: '60px',
                      right: '10px',
                      padding: '13px none',
                      width: 'calc(90% - 90px)',
                      margin: 'auto',
                      zIndex: 100,
                    }}
                  />
                </Stack>
              </>
            )}
          </>
        ) : (
          <Box>
            <Skeleton variant="rect" width="100%" height="100%" />
          </Box>
        )}
      </Box>
    </>
  );
};
